const isString = obj => typeof obj === 'string';
const isFunction = obj => typeof obj === 'function';
const validateName = name => {
  if (!isString(name)) {
    throw new Error(`Name must be a string, but recevied ${typeof name}`);
  }
};
const validateDependencies = dependencies => {
  if (!Array.isArray(dependencies)) {
    throw new Error(`Expected an array of dependencies, but was called with [${typeof dependencies}]`);
  }
};
const validateFactory = (name, fn) => {
  if (!isFunction(fn)) {
    throw new Error(`Factory ${name} must be a [Function]`);
  }
};
const validateArgs = (name, dependencies, fn) => {
  validateName(name);
  validateDependencies(dependencies);
  validateFactory(name, fn);
};
const isPassive = (name, jpex, precedence) => {
  return (precedence || jpex.$$config.precedence) === 'passive' && jpex.$$factories[name] != null;
};
const instantiate = (context, args) => {
  return new (Function.prototype.bind.apply(context, args))();
};
const isNode = () => {
  let _process;
  try {
    _process = new Function('return process')();
  } catch (e) {
  }
  return typeof _process === 'object' && _process.toString && _process.toString() === '[object process]';
};
const doUnsafeRequire = new Function('require', 'target', 'return require.main.require(target)');
const unsafeRequire = target => {
  return doUnsafeRequire(eval('require'), target);
};
const ensureArray = arr => {
  if (arr == null) {
    return [];
  }
  if (Array.isArray(arr)) {
    return arr;
  }
  return [arr];
};
const hasLength = arr => arr == null || arr.length > 0;

function constant(name, obj) {
  validateName(name);
  if (isPassive(name, this)) {
    return;
  }
  this.$$factories[name] = {
    fn: () => obj,
    lifecycle: 'application',
    value: obj,
    resolved: true
  };
}

function factory(name, dependencies, fn, opts = {}) {
  validateArgs(name, dependencies, fn);
  if (!hasLength(dependencies)) {
    dependencies = null;
  }
  if (isPassive(name, this, opts.precedence)) {
    return;
  }
  this.$$factories[name] = {
    fn,
    dependencies,
    lifecycle: opts.lifecycle || this.$$config.lifecycle
  };
  if (opts.alias) {
    ensureArray(opts.alias).forEach(alias => this.alias(alias, name));
  }
}

function service(name, dependencies, fn, opts = {}) {
  validateArgs(name, dependencies, fn);
  function factory(...args) {
    const context = {};
    if (opts.bindToInstance) {
      dependencies.forEach((key, i) => {
        context[key] = args[i];
      });
    }
    return instantiate(fn, [context, ...args]);
  }
  return this.factory(name, dependencies, factory, opts);
}

function alias(alias, name) {
  if (this.$$alias[alias] == null || this.$$config.precedence === 'active') {
    this.$$alias[alias] = name;
  }
  if (this.$$alias[name] == null || this.$$config.precedence === 'active') {
    this.$$alias[name] = alias;
  }
}

const GLOBAL_TYPE_PREFIX = 'type:global:';
const NAMED_PARAMS = '$namedParameters';
const VOID = 'undefined';

const getFromNodeModules = (jpex, target) => {
  if (!jpex.$$config.nodeModules || !isNode()) {
    return;
  }
  try {
    const value = unsafeRequire(target);
    jpex.constant(target, value);
    return jpex.$$factories[target];
  } catch (e) {
    var _e$message;
    if ((_e$message = e.message) != null && _e$message.includes != null && _e$message.includes(`Cannot find module '${target}'`)) {
      return;
    }
    throw e;
  }
};
const getGlobalObject = () => {
  if (typeof global !== VOID) {
    return global;
  }
  if (typeof globalThis !== VOID) {
    return globalThis;
  }
  if (typeof window !== VOID) {
    return window;
  }
  return {};
};
const getGlobalProperty = name => {
  const global = getGlobalObject();
  if (global[name] !== void 0) {
    return global[name];
  }
  if (name.startsWith(GLOBAL_TYPE_PREFIX)) {
    var _global$inferredLower;
    const len = GLOBAL_TYPE_PREFIX.length;
    const inferred = name.substr(len);
    const inferredLower = inferred.charAt(0).toLowerCase() + inferred.substr(1);
    return (_global$inferredLower = global[inferredLower]) != null ? _global$inferredLower : global[inferred];
  }
};
const getFromGlobal = (jpex, name) => {
  if (!jpex.$$config.globals) {
    return;
  }
  const value = getGlobalProperty(name);
  if (value !== void 0) {
    jpex.constant(name, value);
    return jpex.$$factories[name];
  }
};
const getFromAlias = (jpex, alias) => {
  const name = jpex.$$alias[alias];
  if (name != null) {
    return jpex.$$factories[name];
  }
};
const getFromResolved = (jpex, name) => {
  return jpex.$$resolved[name];
};
const getFromRegistry = (jpex, name) => {
  return jpex.$$factories[name];
};
const getFactory = (jpex, name, opts = {}) => {
  var _opts$optional;
  validateName(name);
  const fns = [getFromResolved, getFromRegistry, getFromAlias, getFromGlobal, getFromNodeModules];
  while (fns.length) {
    const factory = fns.shift()(jpex, name);
    if (factory != null) {
      return factory;
    }
  }
  if ((_opts$optional = opts.optional) != null ? _opts$optional : jpex.$$config.optional) {
    return;
  }
  throw new Error(`Unable to find required dependency [${name}]`);
};
const cacheResult = (jpex, name, factory, value, namedParameters, withArg) => {
  switch (factory.lifecycle) {
    case 'application':
      factory.resolved = true;
      factory.value = value;
      factory.with = withArg;
      break;
    case 'class':
      jpex.$$resolved[name] = { ...factory,
        resolved: true,
        value,
        with: withArg
      };
      break;
    case 'none':
      break;
    case 'instance':
    default:
      namedParameters[name] = value;
      break;
  }
};
const checkStack = (jpex, name, stack) => {
  if (!hasLength(stack)) {
    return false;
  }
  if (!stack.includes(name)) {
    return false;
  }
  if (stack[stack.length - 1] === name) {
    var _jpex$$$parent;
    const parent = (_jpex$$$parent = jpex.$$parent) == null ? void 0 : _jpex$$$parent.$$factories[name];
    if (parent != null) {
      return true;
    }
  }
  throw new Error(`Recursive loop for dependency ${name} encountered`);
};

const getNamedParameters = (namedParameters, opts = {}) => {
  if (namedParameters) {
    return namedParameters;
  }
  if (opts.with) {
    return { ...opts.with
    };
  }
  return {};
};
const isResolvedWithParams = (factory, opts = {}) => {
  if (!factory.with && !opts.with) {
    return true;
  }
  const keys = [...new Set([...Object.keys(opts.with || {}), ...Object.keys(factory.with || {})])];
  return keys.every(key => {
    var _opts$with, _factory$with;
    return ((_opts$with = opts.with) == null ? void 0 : _opts$with[key]) === ((_factory$with = factory.with) == null ? void 0 : _factory$with[key]);
  });
};
const resolveFactory = (jpex, name, factory, namedParameters, opts, stack) => {
  if (factory == null) {
    return;
  }
  if (factory.resolved && isResolvedWithParams(factory, opts)) {
    return factory.value;
  }
  let args = [];
  if (hasLength(factory.dependencies)) {
    args = resolveMany(jpex, factory, namedParameters, opts, [...stack, name]);
  }
  const value = factory.fn.apply(jpex, args);
  cacheResult(jpex, name, factory, value, namedParameters, opts == null ? void 0 : opts.with);
  return value;
};
const resolveOne = (jpex, name, initialParameters, opts, stack) => {
  const namedParameters = getNamedParameters(initialParameters, opts);
  if (Object.hasOwnProperty.call(namedParameters, name)) {
    return namedParameters[name];
  }
  if (name === NAMED_PARAMS || name === "type:jpex/NamedParameters") {
    return namedParameters;
  }
  if (checkStack(jpex, name, stack)) {
    return resolveOne(jpex.$$parent, name, namedParameters, opts, []);
  }
  const factory = getFactory(jpex, name, opts);
  return resolveFactory(jpex, name, factory, namedParameters, opts, stack);
};
const resolveMany = (jpex, definition, namedParameters, opts, stack = []) => {
  if (!hasLength(definition.dependencies)) {
    return [];
  }
  const dependencies = ensureArray(definition.dependencies);
  const values = dependencies.map(dependency => {
    return resolveOne(jpex, dependency, namedParameters, opts, stack);
  });
  return values;
};

function resolve(name, opts) {
  return resolveOne(this, name, void 0, opts, []);
}
function resolveDependencies(definition, opts) {
  return resolveMany(this, definition, void 0, opts, []);
}
function isResolved(dependency) {
  var _this$$$factories$dep;
  if (!isString(dependency)) {
    return false;
  }
  if (this.$$resolved[dependency] != null) {
    return true;
  }
  return ((_this$$$factories$dep = this.$$factories[dependency]) == null ? void 0 : _this$$$factories$dep.resolved) === true;
}
function allResolved(dependencies) {
  return dependencies.every(isResolved.bind(this));
}

function encase(dependencies, fn) {
  const jpex = this;
  let result;
  const encased = function encased(...args) {
    if (result && allResolved.call(jpex, dependencies)) {
      return result.apply(this, args);
    }
    const deps = resolveDependencies.call(jpex, {
      dependencies
    });
    result = fn.apply(jpex, deps);
    return result.apply(this, args);
  };
  encased.encased = fn;
  return encased;
}

function clearCache(...names) {
  names = ensureArray(names);
  for (const key in this.$$factories) {
    if (!hasLength(names) || names.includes(key)) {
      this.$$factories[key].resolved = false;
    }
  }
  for (const key in this.$$resolved) {
    if (!hasLength(names) || names.includes(key)) {
      delete this.$$resolved[key];
    }
  }
}

const defaultConfig = {
  lifecycle: 'class',
  precedence: 'active',
  globals: true,
  nodeModules: true,
  optional: false
};
function makeJpex({
  inherit = true,
  ...config
} = {}, parent) {
  const jpex = {
    $$parent: parent,
    $$config: { ...defaultConfig,
      ...(inherit ? parent == null ? void 0 : parent.$$config : {}),
      ...config
    },
    $$factories: parent && inherit ? Object.create(parent.$$factories) : {},
    $$resolved: {},
    $$alias: parent && inherit ? Object.create(parent.$$alias) : {},
    constant,
    factory,
    service,
    alias,
    resolve,
    encase,
    clearCache,
    extend(config) {
      return makeJpex(config, this);
    },
    resolveWith(name, namedParameters, opts) {
      return this.resolve(name, {
        with: namedParameters,
        ...opts
      });
    },
    raw(name) {
      return getFactory(this, name, {}).fn;
    },
    infer: () => ''
  };
  return jpex;
}

const jpex = makeJpex();

export default jpex;
export { jpex };
